/**
 * The "Prüffall" of one move-in run (S1): why Vantio left the address alone,
 * what is the case now, and the admin's answer.
 *
 * ITS OWN COMPONENT on purpose: the run's body (`AutomationRunDetailSections`)
 * renders what the run STORED; this is the one part of the detail that shows
 * something live and the one part that writes, and it should be readable as
 * such on its own.
 *
 * TWO SOURCES, NEVER MIXED.
 *   - A run whose answer it RECORDED (the admin's own `manual` run) carries the
 *     stored answer in its detail (`review_resolution`, 187) — decision, who,
 *     when, and for "kept" the two addresses frozen with the answer. That is
 *     shown as it is; nothing is read for it.
 *   - A review SKIP is the automation's record of an hour in which it did not
 *     change the address. What is shown under it comes from the review read
 *     (`useMoveInReview`): the answers already stored for its contract, and
 *     the tenant's CURRENT open case, labelled "Aktueller Prüffall" because it
 *     is decided now, not when the run happened. The skip itself is never
 *     changed and never re-described.
 *
 * THE ANSWER IS TWO CLICKS. Each action opens a confirmation that says exactly
 * what will happen, with the address and the date of the case the reader saw;
 * the case's fingerprint travels with the answer, so a case that changed in
 * the meantime is refused (409) and read again rather than overwritten. While
 * an answer is on its way both actions are disabled. "Objektadresse
 * übernehmen" exists only where the server allows it (`allowed_actions`): an
 * address the admin maintained on or after the move-in is never overwritten
 * from here.
 *
 * NO ADDRESS IS MADE UP. A `kept_address_protected` case can arrive without a
 * current address (the kept one was deleted since): it says so, offers only
 * what the server allows ("übernehmen", and the way to the tenant), and never
 * shows the old kept copy as if it were current.
 *
 * NO RAW CODE AS COPY. The reason is said in words; the run's code stays where
 * the run's result shows it, small, for support.
 */
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";
import { automationToneClass } from "./automationLabels";
import { automationRunAddressLines } from "./automationRunLabels";

const PRIMARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-primary bg-primary px-3.5 py-1.5 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-9";
const LINK_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] px-2 py-1.5 text-[12.5px] font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9";
const TERM = "text-[11px] text-muted-foreground";

export const MOVE_IN_REVIEW_TITLE = "Wohnadresse bei Mietbeginn nicht geändert";

/** Why the automation left the address alone — per review reason. */
export const MOVE_IN_REVIEW_WHY = {
  manual_review_required:
    "Die aktuelle Wohnadresse hat kein Gültig-ab-Datum und wurde nicht von Vantio erfasst. Vantio kann deshalb nicht sicher erkennen, ob sie vor dem Einzug galt und ändert sie nicht automatisch.",
  manual_address_present:
    "Eine manuell erfasste Wohnadresse gilt am oder nach dem Mietbeginn. Vantio überschreibt sie deshalb nicht automatisch.",
  kept_address_protected:
    "Für dieses Objekt wurde die bestehende Wohnadresse bereits bewusst beibehalten. Vantio ändert sie deshalb bei einem späteren Mietverhältnis nicht automatisch.",
};

export const MOVE_IN_REVIEW_NO_ADDRESS = "Aktuell ist keine Wohnadresse hinterlegt.";

/** The stored answer, as the headline of a closed case. */
export const MOVE_IN_REVIEW_DONE = {
  adopted: "Erledigt — Objektadresse übernommen",
  kept: "Erledigt — Bestehende Adresse beibehalten",
};

const TOAST = {
  adopted: "Prüffall abgeschlossen: Objektadresse übernommen.",
  kept: "Prüffall abgeschlossen: Bestehende Adresse beibehalten.",
};

/** "Musterweg 1a, 8004 Zürich" — one line, in the spelling that was sent. */
function oneLine(address) {
  return automationRunAddressLines(address ? { ...address, country: null } : null).join(", ");
}

function actorName(actor) {
  if (actor && (actor.full_name || actor.email)) return actor.full_name || actor.email;
  return "Benutzer nicht mehr verfügbar";
}

function contractsLabel(tenancies) {
  const refs = (tenancies || []).map((t) => t?.reference_number).filter(Boolean);
  return refs.join(", ");
}

function Pill({ tone, children }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-[11.5px] font-semibold ${automationToneClass(
        tone
      )}`}
    >
      {children}
    </span>
  );
}

function Field({ label, children, wide = false }) {
  if (children === null || children === undefined || children === "" || children === false) {
    return null;
  }
  return (
    <div className={wide ? "min-w-0 sm:col-span-2" : "min-w-0"}>
      <dt className={TERM}>{label}</dt>
      <dd className="m-0 mt-0.5 break-words text-[12.5px] text-foreground">{children}</dd>
    </div>
  );
}

function AddressLines({ address }) {
  const lines = automationRunAddressLines(address);
  if (!lines.length) return null;
  return (
    <address className="not-italic">
      {lines.map((line, i) => (
        <span key={`${i}-${line}`} className="block whitespace-pre-wrap">
          {line}
        </span>
      ))}
    </address>
  );
}

/** The frame every state shares: the question, its state, and why. */
function ReviewFrame({ headingRef, status, reason, busy, children }) {
  const why = MOVE_IN_REVIEW_WHY[reason] || "";
  return (
    <section
      aria-label="Prüffall"
      aria-busy={busy ? "true" : undefined}
      className="flex flex-col gap-2.5 rounded-[12px] border border-border bg-card p-3.5"
    >
      <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Prüffall
      </h3>
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <h4
          ref={headingRef}
          tabIndex={-1}
          className="m-0 min-w-0 break-words text-[13.5px] font-semibold text-foreground focus:outline-none"
        >
          {MOVE_IN_REVIEW_TITLE}
        </h4>
        {status}
      </div>
      {why ? <p className="m-0 text-[12.5px] text-foreground">{why}</p> : null}
      {children}
    </section>
  );
}

/** "Beurteilt ab …" of a stored answer — the date it was frozen with, if any. */
function JudgedFrom({ date }) {
  if (!date) return null;
  return (
    <p className="m-0 text-[12px] text-muted-foreground">
      Beurteilt ab{" "}
      <time dateTime={date} className="text-foreground">
        {formatDateOnly(date)}
      </time>
      : Ein früheres Mietverhältnis am selben Objekt lief bei der Entscheidung noch.
    </p>
  );
}

/** One stored answer: the decision, by whom, when. */
function Answer({ resolution, showContract }) {
  const contract = showContract ? resolution.tenancy?.reference_number : "";
  return (
    <div className="flex flex-col gap-0.5 rounded-[10px] border border-border bg-background px-3 py-2.5">
      <p className="m-0 text-[12.5px] font-semibold text-foreground">
        {MOVE_IN_REVIEW_DONE[resolution.decision] || resolution.decision}
      </p>
      <p className="m-0 text-[12px] text-muted-foreground">
        durch <span className="font-semibold text-foreground">{actorName(resolution.decided_by)}</span>
      </p>
      <p className="m-0 text-[12px] text-muted-foreground">
        am{" "}
        <time dateTime={resolution.decided_at} className="text-foreground">
          {formatDateTime(resolution.decided_at)}
        </time>
      </p>
      {contract ? (
        <p className="m-0 text-[12px] text-muted-foreground">Mietverhältnis {contract}</p>
      ) : null}
      <JudgedFrom date={resolution.judged_from} />
    </div>
  );
}

function TenantLink({ tenantId }) {
  if (!tenantId) return null;
  return (
    <Link to={`/admin/tenants/${encodeURIComponent(tenantId)}`} className={LINK_BUTTON}>
      Zum Mieter
    </Link>
  );
}

/** The admin's own answer run: what it stored, nothing else. */
function StoredAnswer({ run, resolution }) {
  const kept = resolution.decision === "kept";
  return (
    <ReviewFrame
      reason={resolution.review_reason}
      status={<Pill tone="positive">Erledigt</Pill>}
    >
      <Answer
        resolution={{
          decision: resolution.decision,
          decided_by: resolution.decided_by,
          decided_at: resolution.decided_at,
        }}
      />
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2">
        <Field label="Mietbeginn">
          {resolution.move_in_date ? (
            <time dateTime={resolution.move_in_date}>{formatDateOnly(resolution.move_in_date)}</time>
          ) : null}
        </Field>
        <Field label="Mietverhältnis">{contractsLabel(resolution.tenancies) || null}</Field>
        {kept ? (
          <>
            <Field label="Beibehaltene Wohnadresse">
              {resolution.kept_address ? <AddressLines address={resolution.kept_address} /> : null}
            </Field>
            <Field label="Nicht übernommene Objektadresse">
              {resolution.proposed_address ? (
                <AddressLines address={resolution.proposed_address} />
              ) : null}
            </Field>
          </>
        ) : null}
      </dl>
      <JudgedFrom date={resolution.judged_from} />
      {kept ? (
        <p className="m-0 text-[12px] text-muted-foreground">
          Die Adressen sind so gespeichert, wie sie bei der Entscheidung waren.
        </p>
      ) : (
        <p className="m-0 text-[12px] text-muted-foreground">
          Vorher und Nachher stehen unter „Adressänderung“.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <TenantLink tenantId={run.tenant?.id || null} />
      </div>
    </ReviewFrame>
  );
}

/**
 * The tenant's open case, as it is NOW, with the two answers. When today's
 * reason is not the one the run stored, it is said here — inside the box that
 * is labelled as now — and never above it, where the run's own reason stands.
 */
function OpenCase({ reviewCase, runReason, pending, busy, failure, onAsk }) {
  const allowed = reviewCase.allowed_actions || [];
  // `null` only when there is no current address at all: said, never filled in.
  const current = reviewCase.current_address || null;
  const nowWhy =
    reviewCase.reason !== runReason ? MOVE_IN_REVIEW_WHY[reviewCase.reason] || "" : "";
  return (
    <div className="flex flex-col gap-2.5 rounded-[10px] border border-amber-200 bg-amber-50/40 px-3 py-3 dark:border-amber-500/30 dark:bg-amber-500/[0.06]">
      <div>
        <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Aktueller Prüffall
        </p>
        <p className="m-0 mt-0.5 text-[12px] text-muted-foreground">
          Stand jetzt — nicht Teil dieser Ausführung.
        </p>
        {nowWhy ? <p className="m-0 mt-1.5 text-[12.5px] text-foreground">{nowWhy}</p> : null}
      </div>
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2">
        <Field label="Aktuelle Wohnadresse">
          {current ? (
            <>
              <AddressLines address={current} />
              <span className="mt-0.5 block text-[12px] text-muted-foreground">
                {current.valid_from ? (
                  <>
                    Gültig ab{" "}
                    <time dateTime={current.valid_from} className="text-foreground">
                      {formatDateOnly(current.valid_from)}
                    </time>
                  </>
                ) : (
                  "Kein Gültig-ab-Datum erfasst"
                )}
              </span>
            </>
          ) : (
            <span className="block">{MOVE_IN_REVIEW_NO_ADDRESS}</span>
          )}
        </Field>
        <Field label="Vorgeschlagene Objektadresse">
          <AddressLines address={reviewCase.proposed_address} />
        </Field>
        <Field label="Mietbeginn">
          <time dateTime={reviewCase.move_in_date}>{formatDateOnly(reviewCase.move_in_date)}</time>
        </Field>
        <Field label="Mietverhältnis">{contractsLabel(reviewCase.tenancies) || null}</Field>
      </dl>
      {reviewCase.judged_from ? (
        // The safety rule judged the current address against an earlier date
        // than this contract's own move-in: an earlier contract on the same
        // object is still running. Said, so the reason does not read as wrong;
        // an "adopt" still applies from the Mietbeginn above.
        <p className="m-0 text-[12px] text-muted-foreground">
          Beurteilt ab{" "}
          <time dateTime={reviewCase.judged_from} className="text-foreground">
            {formatDateOnly(reviewCase.judged_from)}
          </time>
          : Ein früheres Mietverhältnis am selben Objekt läuft noch.
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {failure.message}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {allowed.includes("adopt") ? (
          <button
            type="button"
            className={PRIMARY_BUTTON}
            disabled={busy}
            onClick={() => onAsk("adopt")}
          >
            {pending === "adopt" ? "Wird übernommen …" : "Objektadresse übernehmen"}
          </button>
        ) : null}
        {allowed.includes("keep") ? (
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={busy}
            onClick={() => onAsk("keep")}
          >
            {pending === "keep" ? "Wird gespeichert …" : "Bestehende Adresse beibehalten"}
          </button>
        ) : null}
        <TenantLink tenantId={reviewCase.tenant_id} />
      </div>
    </div>
  );
}

/** A review skip: stored answers for its contract and the current case. */
function LiveReview({ run, review }) {
  const headingRef = useRef(null);
  // What the confirmation is about — captured when it opened, so the words
  // it shows and the fingerprint it sends are the ones of the case the reader
  // asked about, even if the case is read again while it is open.
  // Kept after closing, so the dialog keeps its own words while it fades out.
  const [confirm, setConfirm] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const opener = useRef(null);
  const confirmed = useRef(false);

  const data = review.review;
  const reviewCase = data?.case || null;
  const resolutions = Array.isArray(data?.resolutions) ? data.resolutions : [];

  const ask = (decision) => {
    if (!reviewCase || review.busy) return;
    opener.current = typeof document !== "undefined" ? document.activeElement : null;
    confirmed.current = false;
    setConfirm({
      decision,
      fingerprint: reviewCase.fingerprint,
      target: oneLine(reviewCase.proposed_address),
      moveIn: reviewCase.move_in_date,
      hasCurrent: !!reviewCase.current_address,
    });
    setConfirmOpen(true);
  };

  const answer = async () => {
    if (!confirm) return;
    confirmed.current = true;
    const { decision, fingerprint } = confirm;
    const result = await review.resolve(decision, fingerprint);
    if (result) toast.success(TOAST[result.decision] || "Prüffall abgeschlossen.");
  };

  // Back to what opened the confirmation on "Abbrechen"; after an answer the
  // buttons are disabled or gone, so the question's heading takes focus and
  // the reader hears the new state next.
  const returnFocus = (event) => {
    event.preventDefault();
    const back = opener.current;
    opener.current = null;
    if (!confirmed.current && back && back.isConnected && !back.disabled) {
      back.focus();
      return;
    }
    headingRef.current?.focus();
  };

  // A run that named ONE contract gets that contract's answers, and they close
  // its question. A run that judged several names none (`tenancy_id` NULL):
  // the answers then are the tenant's, each shown with its contract, and none
  // of them is claimed as THIS run's answer.
  const scoped = !!run.tenancy_id;

  let status = null;
  if (reviewCase) status = <Pill tone="warning">Prüfung erforderlich</Pill>;
  else if (scoped && resolutions.length) status = <Pill tone="positive">Erledigt</Pill>;
  else if (data) status = <Pill tone="muted">Keine Prüfung offen</Pill>;

  // The question above is the run's: its STORED reason, never today's.
  const reason = run.skip_reason;
  const showContract = !scoped || resolutions.length > 1;

  let body;
  if (review.loading && !data) {
    body = (
      <p role="status" className="m-0 text-[12.5px] text-muted-foreground">
        Prüffall wird geladen …
      </p>
    );
  } else if (review.error && !data) {
    body = (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {review.error}
        </p>
        <button type="button" onClick={review.reload} className={SECONDARY_BUTTON}>
          Erneut versuchen
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {!scoped && resolutions.length ? (
          <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Gespeicherte Antworten für Mietverhältnisse dieses Mieters
          </p>
        ) : null}
        {resolutions.map((resolution) => (
          <Answer
            key={`${resolution.tenancy?.id}-${resolution.decided_at}`}
            resolution={resolution}
            showContract={showContract}
          />
        ))}
        {reviewCase ? (
          <OpenCase
            reviewCase={reviewCase}
            runReason={run.skip_reason}
            pending={review.pending}
            busy={review.busy}
            failure={review.failure}
            onAsk={ask}
          />
        ) : (
          <>
            {review.failure ? (
              <p role="alert" className="m-0 text-[12.5px] text-destructive">
                {review.failure.message}
              </p>
            ) : null}
            {!scoped || !resolutions.length ? (
              <p className="m-0 text-[12.5px] text-muted-foreground">
                Aktuell ist für diesen Mieter keine Prüfung offen.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <TenantLink tenantId={run.tenant?.id || null} />
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <ReviewFrame headingRef={headingRef} status={status} reason={reason} busy={!!review.pending}>
      {body}
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) setConfirmOpen(false);
        }}
      >
        <AlertDialogContent onCloseAutoFocus={returnFocus}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.decision === "adopt"
                ? "Objektadresse übernehmen?"
                : "Bestehende Adresse beibehalten?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.decision === "adopt"
                ? `Die Wohnadresse wird ab ${formatDateOnly(confirm?.moveIn)} auf ${
                    confirm?.target
                  } gesetzt.${
                    confirm?.hasCurrent ? " Die bisherige Adresse bleibt im Verlauf erhalten." : ""
                  }`
                : "Die bestehende Wohnadresse bleibt unverändert. Vantio markiert diesen Prüffall als bewusst abgeschlossen."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={answer}>
              {confirm?.decision === "adopt" ? "Übernehmen" : "Beibehalten"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ReviewFrame>
  );
}

export default function AutomationMoveInReview({ run, review }) {
  if (!run) return null;
  if (run.review_resolution) {
    return <StoredAnswer run={run} resolution={run.review_resolution} />;
  }
  if (!review || !review.wanted) return null;
  return <LiveReview run={run} review={review} />;
}
